// src/components/author/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Inbox,
  ArrowLeft,
  Mail,
  Info,
  PenTool,
  MessageSquare,
  Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';
import {
  formatStatus,
  formatDate,
  getStatusClass,
  getStatusIcon,
  StatusBadge
} from '../../utils/utils.global';

// Truncate long titles cleanly (keeps whole words where possible)
const truncateTitle = (title, max = 90) => {
  if (!title) return '';
  const str = String(title).trim();
  if (str.length <= max) return str;
  const cut = str.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    submitted: 0,
    inReview: 0,
    coAuthored: 0,
    withDecisions: 0,
    inbox: 0,
    returned: 0,
    draft: 0
  });

  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [revisionsCount, setRevisionsCount] = useState(0);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const statsResponse = await axios.get('/api/authors/dashboard/stats');
      if (statsResponse.data.status === 'success') {
        setStats(statsResponse.data.stats);
      }

      const recentResponse = await axios.get('/api/authors/dashboard/recent');
      if (recentResponse.data.status === 'success') {
        setRecentSubmissions(recentResponse.data.submissions);

        const revisionCount = recentResponse.data.submissions.filter(
          (sub) =>
            sub.status === 'returned_for_revision' ||
            sub.status === 'returned_for_correction'
        ).length;
        setRevisionsCount(revisionCount);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      showToast('Failed to load dashboard data. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(`/manuscripts?a=${id}`);
  };

  const statCards = [
    {
      label: 'Submitted Manuscripts',
      value: stats.submitted,
      icon: FileText,
      color: 'bg-purple-100',
      iconColor: 'text-purple-600',
      link: '/manuscripts',
      bgClass: 'bg-purple-50'
    },
    {
      label: 'Submit Returning Manuscript',
      value: stats.withDecisions,
      icon: CheckCircle,
      color: 'bg-blue-100',
      iconColor: 'text-blue-600',
      link: '/inreview',
      bgClass: 'bg-blue-50'
    },
    {
      label: 'Manuscripts Co-Authored',
      value: stats.coAuthored,
      icon: Users,
      color: 'bg-green-100',
      iconColor: 'text-green-600',
      link: '/coauthored',
      bgClass: 'bg-green-50'
    },
    {
      label: 'Returned Manuscripts',
      value: stats.returned,
      icon: ArrowLeft,
      color: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      link: '/inreview?returned=true',
      bgClass: 'bg-yellow-50'
    },
    {
      label: 'In Review',
      value: stats.inReview,
      icon: Clock,
      color: 'bg-orange-100',
      iconColor: 'text-orange-600',
      link: '/manuscripts?status=review',
      bgClass: 'bg-orange-50'
    },
    {
      label: 'Inbox',
      value: stats.inbox,
      icon: Inbox,
      color: 'bg-red-100',
      iconColor: 'text-red-600',
      link: '/messages/inbox',
      bgClass: 'bg-red-50'
    }
  ];

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1">
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Editorial Office Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Info className="w-5 h-5 text-blue-500" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-blue-800 flex items-center">
                  Editorial Office <PenTool className="w-4 h-4 ml-2 text-blue-600" />
                </h3>
                <p className="mt-1 text-blue-700">
                  For assistance, please contact{' '}
                  <a
                    href="mailto:submissions@asfirj.org"
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    submissions@asfirj.org
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Welcome Alert */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Info className="w-5 h-5 text-purple-500" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-purple-800">AUTHORS</h3>
                <p className="mt-1 text-purple-700">
                  Click on Author in the navigation bar above to access your Authors
                  Dashboard.
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Welcome back, {user?.firstName || 'Researcher'}
            </h2>
            <p className="text-gray-600 mt-1">
              Manage your submissions and track your research progress
            </p>
          </div>

          {/* Dashboard Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {statCards.map((stat, index) => (
              <Link
                key={index}
                to={stat.link}
                className="dashboard-card bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${stat.color} p-3 rounded-lg`}>
                    <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                  </div>
                  <div className="ml-4 flex-1">
                    <h6 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {stat.label}
                    </h6>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-2xl font-bold text-[--asfi-purple]">
                        {stat.value}
                      </span>
                      <span className="text-sm text-gray-400 hover:text-[--asfi-blue]">
                        View →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Recent Submissions */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                Recent Submissions
              </h3>
              <Link
                to="/manuscripts"
                className="text-sm text-[--asfi-blue] hover:text-[--asfi-purple] font-medium flex items-center"
              >
                View all
                <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
              </Link>
            </div>

            <div className="space-y-4">
              {recentSubmissions.map((submission) => {
                const submissionId = submission.revision_id || submission.id;
                const displayTitle = truncateTitle(submission.title, 110);
                const fullTitle = submission.title || '';
                return (
                  <div
                    key={submissionId}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 mb-3 md:mb-0 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-medium text-[--asfi-purple] bg-purple-50 px-2 py-1 rounded">
                          {submission.type || submission.article_type || 'Research Article'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {submissionId}
                        </span>
                      </div>

                      <p
                        className="font-medium text-gray-900 break-words"
                        title={fullTitle}
                      >
                        {displayTitle || fullTitle}
                      </p>
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusClass(
                          submission.status
                        )}`}
                      >
                        {getStatusIcon(submission.status)}
                        {formatStatus(submission.status)}
                      </span>
                      <StatusBadge status={submission.status} />

                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        {submission.date
                          ? formatDate(submission.date)
                          : formatDate(
                              submission.date_submitted || submission.process_start_date
                            )}
                      </span>

                      <button
                        className="text-[--asfi-blue] hover:text-[--asfi-purple] text-sm font-medium whitespace-nowrap"
                        onClick={() => handleView(submissionId)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {recentSubmissions.length === 0 && (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No recent submissions</p>
                <Link
                  to="/submit"
                  className="inline-block mt-3 text-[--asfi-blue] hover:text-[--asfi-purple] font-medium"
                >
                  Submit your first manuscript →
                </Link>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {revisionsCount > 0 && (
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-purple-200 p-2 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-purple-700" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2">
                      Need to make revisions?
                    </h3>
                    <p className="text-purple-700 mb-4">
                      You have {revisionsCount} manuscript
                      {revisionsCount !== 1 ? 's' : ''} requiring revisions based on
                      reviewer feedback.
                    </p>
                    <Link
                      to="/inreview?status=revision"
                      className="inline-block px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors"
                    >
                      View Revisions
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div
              className={`${
                revisionsCount > 0 ? '' : 'md:col-span-2'
              } bg-blue-50 rounded-xl p-6 border border-blue-100`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-blue-200 p-2 rounded-lg">
                  <PenTool className="w-5 h-5 text-blue-700" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Start a new submission
                  </h3>
                  <p className="text-blue-700 mb-4">
                    Share your research with the global scientific community.
                  </p>
                  <Link
                    to="/submit"
                    className="inline-block px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-[--asfi-purple] transition-colors"
                  >
                    New Submission
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Support */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Need help? Contact our support team at{' '}
              <a
                href="mailto:support@asfirj.org"
                className="text-[--asfi-blue] hover:text-[--asfi-purple] font-medium"
              >
                support@asfirj.org
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;